import React, { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const TESTIMONIALS = [
  {
    text: "Este conteúdo me trouxe direção em um momento em que eu precisava tomar decisões importantes. Foi mais do que leitura, foi alinhamento com propósito.",
    author: "Junior Alves",
  },
  {
    text: "Encontrei aqui clareza para organizar minha vida espiritual e minhas responsabilidades. Simples, direto e transformador.",
    author: "Ronaldo Farias",
  },
  {
    text: "Em meio a dias difíceis, essas palavras foram como um direcionamento de Deus para mim. Trouxeram paz e firmeza.",
    author: "Janaina Figueiredo",
  },
  {
    text: "Cada mensagem carrega verdade e profundidade. Não é apenas conteúdo, é algo que realmente toca e transforma.",
    author: "Nanda Costa",
  },
];

const ROTATION_MS = 7000;
const FADE_MS = 600;

export default function Testimonials({ variant = "section", className = "" }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const r = useReveal();
  const isCompact = variant === "compact";

  useEffect(() => {
    if (paused) return undefined;
    const fadeOut = setTimeout(() => setVisible(false), ROTATION_MS - FADE_MS);
    const next = setTimeout(() => {
      setIndex((i) => (i + 1) % TESTIMONIALS.length);
      setVisible(true);
    }, ROTATION_MS);
    return () => {
      clearTimeout(fadeOut);
      clearTimeout(next);
    };
  }, [index, paused]);

  const t = TESTIMONIALS[index];

  if (isCompact) {
    return (
      <div
        ref={r}
        data-testid="testimonials-compact"
        className={`reveal ${className}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex items-center gap-3 mb-4">
          <Quote
            size={16}
            strokeWidth={1.4}
            className="text-[#C5A059]"
            aria-hidden
          />
          <span className="text-[0.66rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium">
            Leitores
          </span>
        </div>
        <div
          data-testid="testimonial-card"
          className="relative min-h-[140px] md:min-h-[130px]"
          aria-live="polite"
        >
          <div
            key={index}
            data-testid={`testimonial-${index}`}
            className="transition-opacity ease-out"
            style={{
              opacity: visible ? 1 : 0,
              transitionDuration: `${FADE_MS}ms`,
            }}
          >
            <p
              data-testid="testimonial-text"
              className="font-serif-display italic text-[1.05rem] md:text-[1.15rem] text-[#0B1E36]/85 leading-[1.55] tracking-[-0.008em]"
            >
              &ldquo;{t.text}&rdquo;
            </p>
            <div className="mt-4 flex items-center gap-3">
              <span className="h-px w-6 bg-[#C5A059]" aria-hidden />
              <span
                data-testid="testimonial-author"
                className="text-[0.68rem] uppercase tracking-[0.26em] text-[#0B1E36]/65 font-medium"
              >
                {t.author}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2" role="tablist">
          {TESTIMONIALS.map((_, i) => {
            const active = i === index;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Depoimento ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => {
                  setVisible(false);
                  setTimeout(() => {
                    setIndex(i);
                    setVisible(true);
                  }, FADE_MS / 2);
                }}
                className={`h-[5px] rounded-full transition-all duration-500 ${
                  active
                    ? "w-6 bg-[#C5A059]"
                    : "w-1.5 bg-[#0B1E36]/15 hover:bg-[#0B1E36]/35"
                }`}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={r}
      data-testid="testimonials-section"
      className={`reveal mt-24 md:mt-28 border-t border-[#0B1E36]/10 pt-16 md:pt-20 ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="flex items-center justify-center mb-8">
          <span className="accent-rule" />
          <span className="eyebrow">Depoimentos de leitores</span>
        </div>

        <div
          data-testid="testimonial-card"
          className="relative min-h-[220px] md:min-h-[200px]"
          aria-live="polite"
        >
          <Quote
            size={32}
            strokeWidth={1.2}
            className="text-[#C5A059]/60 mx-auto mb-6"
            aria-hidden
          />
          <div
            key={index}
            data-testid={`testimonial-${index}`}
            className="transition-opacity ease-out"
            style={{
              opacity: visible ? 1 : 0,
              transitionDuration: `${FADE_MS}ms`,
            }}
          >
            <p
              data-testid="testimonial-text"
              className="font-serif-display italic text-[1.35rem] md:text-[1.65rem] lg:text-[1.85rem] text-[#0B1E36]/90 leading-[1.45] tracking-[-0.012em]"
            >
              &ldquo;{t.text}&rdquo;
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-[#C5A059]" aria-hidden />
              <span
                data-testid="testimonial-author"
                className="text-[0.72rem] uppercase tracking-[0.3em] text-[#0B1E36]/70 font-medium"
              >
                {t.author}
              </span>
            </div>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="mt-10 flex items-center justify-center gap-2.5" role="tablist">
          {TESTIMONIALS.map((_, i) => {
            const active = i === index;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Depoimento ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => {
                  setVisible(false);
                  setTimeout(() => {
                    setIndex(i);
                    setVisible(true);
                  }, FADE_MS / 2);
                }}
                className={`h-[6px] rounded-full transition-all duration-500 ${
                  active
                    ? "w-8 bg-[#C5A059]"
                    : "w-2 bg-[#0B1E36]/20 hover:bg-[#0B1E36]/40"
                }`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
